/* eslint-disable react-hooks/exhaustive-deps */
import { CardContent, Paper, Stack, Switch, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreditCardIcon from '@mui/icons-material/CreditCard';

import { TransactionTypeOption } from '../../features/Transactions/TransactionTypeSelector';
import { MinusIcon, PlusIcon, TransferIcon, VerticalAlignBottomIcon, VerticalAlignTopIcon, EventRepeatIcon, Checkbook } from '../../ui/icons';
import { FormFieldUnderline } from '../../ui/FormField/FormFieldUnderline';
import CurrencyField from "../../ui/CurrencyField";
import useThrottle from "../../../hooks/useThrottle";

import ExcludeFromReportToggle from "./components/ExcludeFromReportToggle";
import SubmitButton from "./components/SubmitButton";
import TransactionTypeSelectorButton from "./components/TransactionTypeSelectorButton";
import TransactionFormDialogs from "./components/TransactionFormDialogs";
import TransactionFormContent from "./components/TransactionFormContent";
import { TransactionType } from "./components/types";
import {
  useTransactionFormState,
  useTransactionSubmit,
  useTransactionFormEffects,
} from "./hooks";
import {
  mapEnumToUrlTab,
  getTransactionTypeLabelConfig,
  getTransactionTypeIconConfig,
  getTransactionTypeColors,
  getTransactionTypeConfigKey,
  TransactionIconType,
} from "./helpers";

interface BasicTransactionFormsProps {
  isDrawer?: boolean;
  handleCloseDrawer?: () => void;
}

const BasicTransactionForms = ({ isDrawer = false, handleCloseDrawer }: BasicTransactionFormsProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'transactions', 'forms', 'transactionTypeSelector', 'installments']);

  // Initialize form state
  const formState = useTransactionFormState();
  const {
    transaction,
    isUpdate,
    currentTab,
    transactionTypeConfirmed,
    transactionTypeConfirmedRef,
    loanType,
    creditCardType,
    amount,
    setAmount,
    amountError,
    isExcludedFromReport,
    setIsExcludedFromReport,
    amountInputRef,
    setCurrentTransactionType,
    setLoanType,
    setCreditCardType,
    setTransactionTypeConfirmed,
    setOpenTransactionTypeSelector,
    resetWallets,
    clearResettingWalletsFlag,
    isManuallyOpeningTypeSelectorRef,
    expenseWallet,
    isInstallment,
    setIsInstallment,
    numberOfInstallments,
    setNumberOfInstallments,
    installmentFee,
    setInstallmentFee,
  } = formState;

  // Initialize effects
  const { selectorCallback, handleAmountFocus } = useTransactionFormEffects(formState);

  // Initialize submit handlers
  const { submitTransaction, updateTransaction } = useTransactionSubmit({
    formState,
    isDrawer,
    handleCloseDrawer,
  });

  // Get form content renderers
  const { renderExpenseForm, renderIncomeForm, renderTransferForm, renderLoanForm } = TransactionFormContent({ formState });

  // Icon mappings
  const iconMap: Record<TransactionIconType, React.ReactNode> = {
    chevron: <ChevronRightIcon />,
    verticalBottom: <VerticalAlignBottomIcon />,
    verticalTop: <VerticalAlignTopIcon />,
    creditCard: <CreditCardIcon />,
    minus: <MinusIcon />,
    plus: <PlusIcon />,
    transfer: <TransferIcon />,
  };

  const getTransactionTypeLabel = () => {
    if (!transactionTypeConfirmed) {
      return t('select_type', { ns: 'transactionTypeSelector', defaultValue: 'Chọn loại giao dịch' });
    }
    const config = getTransactionTypeLabelConfig(currentTab, loanType, creditCardType);
    return t(config.key, { ns: 'transactionTypeSelector', defaultValue: config.defaultValue });
  };

  const getTransactionTypeIcon = () => {
    if (!transactionTypeConfirmed) {
      return { icon: iconMap.chevron, bgColor: theme.palette.grey[400], iconColor: theme.palette.common.white };
    }
    const configKey = getTransactionTypeConfigKey(currentTab, loanType, creditCardType);
    const iconConfig = getTransactionTypeIconConfig(currentTab, loanType, creditCardType);
    const colors = getTransactionTypeColors(configKey, theme);
    return { icon: iconMap[iconConfig.iconType], bgColor: colors.bgColor, iconColor: colors.iconColor };
  };

  // Handler for mousedown on Transaction Type button
  // This sets a flag BEFORE useClickOutside fires, so selectorCallback knows to skip
  const handleTransactionTypeMouseDown = () => {
    isManuallyOpeningTypeSelectorRef.current = true;
  };

  const handleTransactionTypeSelect = (option: TransactionTypeOption) => {
    // Clear the manual opening flag now that user has selected a type
    isManuallyOpeningTypeSelectorRef.current = false;

    // Check if transaction type actually changed
    const isTypeChanged = option.type !== currentTab;
    const isLoanTypeChanged = option.type === TransactionType.LOAN && option.loanType !== loanType;
    const isCreditCardTypeChanged = option.type === TransactionType.CREDIT_CARD && option.creditCardType !== creditCardType;
    const hasChanged = isTypeChanged || isLoanTypeChanged || isCreditCardTypeChanged;

    // Reset wallets only when transaction type or subtype changes
    if (hasChanged) {
      resetWallets();
    }

    // Update transaction type
    setCurrentTransactionType(mapEnumToUrlTab(option.type));

    // Update loan type (set to the new value or null if not a loan type)
    setLoanType(option.type === TransactionType.LOAN ? option.loanType || null : null);

    // Update credit card type (set to the new value or null if not a credit card type)
    setCreditCardType(option.type === TransactionType.CREDIT_CARD ? option.creditCardType || null : null);

    transactionTypeConfirmedRef.current = true;
    setTransactionTypeConfirmed(true);

    // Clear the resetting flag after state updates are scheduled
    // Use setTimeout to ensure this runs after React batches the state updates
    if (hasChanged) {
      setTimeout(clearResettingWalletsFlag, 0);
    }
  };

  const handleDisable = () => {
    if (amountError || !amount) return true;
    const { datePicker, spendingCategory, incomeCategory, expenseWallet, incomeWallet, transferSourceWallet, selectDestinationWallet, loanWallet, numberOfInstallments, lendWallets, lendExpendWallet, remainingAmount } = formState;

    const hasDate = !!datePicker;
    const hasExpenseFields = expenseWallet?.wallet?.id && spendingCategory?.id && hasDate;
    const hasTransferFields = transferSourceWallet?.wallet?.id && selectDestinationWallet?.wallet?.id && hasDate;

    switch (currentTab) {
      case TransactionType.EXPENSE:
        return !hasExpenseFields;
      case TransactionType.INCOME:
        return !(incomeWallet?.wallet?.id && incomeCategory?.id && hasDate);
      case TransactionType.TRANSFER:
        return !hasTransferFields;
      case TransactionType.CREDIT_CARD:
        if (creditCardType === 'convert_installment') {
          return !(selectDestinationWallet?.wallet?.id && numberOfInstallments && hasDate);
        }
        return !hasTransferFields;
      case TransactionType.LOAN:
        if (loanType === 'borrow-expense') return !hasExpenseFields;
        if (loanType === 'lend') {
          return !(Array.isArray(lendWallets) && lendWallets.length > 0 &&
            (!lendExpendWallet || (spendingCategory && lendExpendWallet)) && hasDate);
        }
        return !(loanWallet?.wallet?.id && (remainingAmount === undefined || remainingAmount >= 0));
      default:
        return false;
    }
  };

  const onSubmit = useThrottle(async () => {
    if (transaction) {
      // Unified API handles type changes automatically
      updateTransaction();
    } else {
      submitTransaction();
    }
  }, 1000);

  return (
    <>
      <Stack useFlexGap alignItems={'center'} spacing={4} pb={12}>
        <Paper elevation={0} sx={{ borderRadius: 2, width: "100%", px: 4 }}>
          <CurrencyField
            name="amount"
            value={amount}
            variant="standard"
            inputRef={amountInputRef}
            onFocus={handleAmountFocus}
            onChange={(e) => setAmount(Number(e.target.value) || null)}
            onConfirm={(val) => {
              setAmount(Math.abs(Number(val) || 0) || null);
              if (!!val) selectorCallback();
            }}
            error={!!amountError}
            helperText={amountError}
          />
        </Paper>

        <TransactionTypeSelectorButton
          label={getTransactionTypeLabel()}
          icon={getTransactionTypeIcon().icon}
          bgColor={getTransactionTypeIcon().bgColor}
          iconColor={getTransactionTypeIcon().iconColor}
          disabled={transaction?.type === 'borrow-expense'}
          isSelected={transactionTypeConfirmed}
          placeholder={t('transaction_type', { ns: 'transactionTypeSelector', defaultValue: 'Loại giao dịch' })}
          onClick={() => setOpenTransactionTypeSelector(true)}
          onMouseDown={handleTransactionTypeMouseDown}
        />

        {transactionTypeConfirmed && (
          <Paper elevation={!isDrawer ? 3 : 0} sx={{ py: 1, width: "100%", borderRadius: 2 }}>
            <CardContent>
              {currentTab === TransactionType.EXPENSE && renderExpenseForm()}
              {currentTab === TransactionType.INCOME && renderIncomeForm()}
              {currentTab === TransactionType.TRANSFER && renderTransferForm()}
              <div key={`${currentTab}-${loanType}-${creditCardType}`}>
                {renderLoanForm()}
              </div>

              <ExcludeFromReportToggle
                label={t('details.nonreportable_label', { ns: 'transactions' })}
                checked={isExcludedFromReport}
                onChange={setIsExcludedFromReport}
              />
            </CardContent>
          </Paper>
        )}

        {/* Installment Conversion Section - separate card for credit card expenses */}
        {transactionTypeConfirmed &&
          (currentTab === TransactionType.EXPENSE || loanType === 'borrow-expense') &&
          expenseWallet?.wallet?.type === 'credit' && (
            <Paper elevation={!isDrawer ? 3 : 0} sx={{ py: 1, width: "100%", borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={4}>
                  <Stack
                    direction="row"
                    spacing={theme.spacing(2)}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body1" color="secondary">
                      {t('conversion', { ns: 'installments' })}
                    </Typography>
                    <Switch
                      checked={isInstallment}
                      onChange={(e) => setIsInstallment(e.target.checked)}
                    />
                  </Stack>

                  {isInstallment && (
                    <>
                      <FormFieldUnderline
                        type="number"
                        onChange={(e) =>
                          setNumberOfInstallments(Number.parseInt(e.target.value))
                        }
                        value={numberOfInstallments}
                        icon={<EventRepeatIcon fontSize='30px' />}
                        label={t('number_of_installments_label', { ns: 'installments' })}
                        placeholder={t('number_of_installments_placeholder', { ns: 'installments' })}
                      />
                      <FormFieldUnderline
                        type="number"
                        onChange={(e) => setInstallmentFee(Number.parseInt(e.target.value))}
                        value={installmentFee}
                        icon={<Checkbook fontSize='30px' />}
                        label={t('fee', { ns: 'installments' })}
                        placeholder={t('fee_placeholder', { ns: 'installments' })}
                      />
                    </>
                  )}
                </Stack>
              </CardContent>
            </Paper>
          )}
      </Stack>

      <SubmitButton
        isUpdate={isUpdate}
        disabled={handleDisable()}
        isDrawer={isDrawer}
        submitLabel={t('add_transaction', { ns: 'transactions' })}
        updateLabel={t('save_transaction', { ns: 'transactions' })}
        onClick={onSubmit}
      />

      <TransactionFormDialogs
        formState={formState}
        onTransactionTypeSelect={handleTransactionTypeSelect}
      />
    </>
  );
};

export default BasicTransactionForms;
